import { defineStore } from "pinia";
import planningAPI from "@/services/planningAPI";
import { ref } from "vue";

export const usePlanningStore = defineStore("planningStore", {
  state: () => ({
    createPlanStatus: ref({
      message: null,
      isError: false,
      code: null,
    }),
    createPlanLoading: ref(false),
    getPlanningsStatus: ref({
      message: null,
      code: null,
    }),
    getPlanningsLoading: ref(false),
    plannings: ref([]),
    updatePlanningStatus: ref({
      message: null,
      code: null,
    }),
    updatePlanningLoading: ref(false),
    deletePlanningStatus: ref({
      message: null,
      code: null,
    }),
    deletePlanningLoading: ref(false),
    getPlanningStatus: ref({
      message: null,
      code: null,
    }),
    getPlanningLoading: ref(false),
    planning: ref([]),
    getProgramDetailStatus: ref({
      message: null,
      code: null,
    }),
    getProgramDetailLoading: ref(false),
    programDetail: ref([]),
  }),
  actions: {
    async createPlan(data) {
      this.createPlanLoading = true;
      try {
        const res = await planningAPI.createPlan(data);
        console.log(res);
        this.createPlanLoading = false;
        this.createPlanStatus.isError = false;
        this.createPlanStatus.message = "Plan Created";
        this.createPlanStatus.code = res.data.status;
      } catch (err) {
        console.error(err);
        this.createPlanLoading = false;
        this.createPlanStatus.isError = true;
        this.createPlanStatus.message = err.response.data.message;
        this.createPlanStatus.code = err.response.data.status;
        return err;
      }
    },
    async getPlannings(params) {
      this.getPlanningsLoading = true;
      try {
        const res = await planningAPI.getPlannings(params);
        console.log("[Planning Store] getPlannings API response:", res.data);

        const rawPlannings = res.data.data.plannings;
        const transformedPlannings = rawPlannings.flatMap(({ date, program }) =>
          date.map(({ start, end }) => ({ program, start, end }))
        );

        this.plannings = transformedPlannings.map((data) => {
          const start = data.start;
          const end = data.end;
          const programName = "Program " + data.program.replace(/^\D+/g, "");
          const id = data.program.replace(/^\D+/g, "");
          return { start, end, programName, id };
        });

        this.getPlanningsStatus.message = "Planning Fetched";
        this.getPlanningsStatus.code = res.data.status;
      } catch (err) {
        console.error("[Planning Store] Error in getPlannings:", err);
        this.createPlanStatus.message =
          err.response?.data?.error || "Unknown error";
        this.createPlanStatus.code = err.response?.data?.status || "No status";
        return err;
      } finally {
        this.getPlanningsLoading = false;
      }
    },

    async getPlanning(id, params) {
      this.getPlanningLoading = true;
      try {
        const res = await planningAPI.getPlanning(id, params);

        const planningData = res.data.data.planning;

        this.planning.startTime = planningData.startTime.filter(
          (data) => data.enable === "1"
        );
        this.planning.stations = planningData.stations;

        this.planning.stations.forEach((data) => {
          this.planning.mode = data.mode === "minute/second" ? 1 : 2;

          if (typeof data.value === "string" && data.value.includes(".")) {
            const [digit1, digit2] = data.value.split(".");
            data.timeDigit1 = digit1;
            data.timeDigit2 = digit2;
          } else {
            data.timeDigit1 = data.value || "0";
            data.timeDigit2 = "00";
          }
        });

        this.planning.isStationEmpty = planningData.stations.length === 0;

        this.getPlanningStatus.message = "Planning Fetched";
      } catch (err) {
        console.error("[Planning Store] Error in getPlanning:", err);
        return err;
      } finally {
        this.getPlanningLoading = false;
      }
    },

    async getProgramDetail(id, params) {
      this.getProgramDetailLoading = true;
      try {
        const res = await planningAPI.getProgramDetail(id, params);
        console.log(
          "[Planning Store] getProgramDetail API response:",
          res.data
        );
        console.log(
          `[Planning Store] getProgramDetail - API call successful, status: ${res.status}`
        );

        const planning = res.data.data.planning;
        console.log(
          `[Planning Store] getProgramDetail - Raw planning data:`,
          planning
        );

        this.programDetail.programName = planning.programName;
        console.log(
          `[Planning Store] getProgramDetail - Set programName: ${this.programDetail.programName}`
        );

        this.programDetail.startTime = planning.startTime.filter(
          (data) => data.enable === "1"
        );
        console.log(
          `[Planning Store] getProgramDetail - Filtered startTime:`,
          this.programDetail.startTime
        );

        this.programDetail.stations = planning.stations;
        console.log(
          `[Planning Store] getProgramDetail - Raw stations:`,
          this.programDetail.stations
        );

        this.programDetail.stations.map((data) => {
          this.programDetail.mode = data.mode === "minute/second" ? 1 : 2;
          data.timeDigit1 = data.value.split(".")[0];
          data.timeDigit2 = data.value.split(".")[1];
          console.log(
            `[Planning Store] getProgramDetail - Processed station:`,
            {
              mode: this.programDetail.mode,
              timeDigit1: data.timeDigit1,
              timeDigit2: data.timeDigit2,
              originalValue: data.value,
            }
          );
        });

        this.programDetail.isStationEmpty = planning.stations.length === 0;
        console.log(
          `[Planning Store] getProgramDetail - isStationEmpty: ${this.programDetail.isStationEmpty}`
        );

        console.log(
          "[Planning Store] Final programDetail object:",
          this.programDetail
        );

        this.getProgramDetailStatus.message = "Program Fetched";
        console.log(
          `[Planning Store] getProgramDetail - Status message set: ${this.getProgramDetailStatus.message}`
        );
        // Optionally uncomment:
        // this.getProgramDetailStatus.code = res.data.status;
      } catch (err) {
        console.error("[Planning Store] Error in getProgramDetail:", err);
        console.error(`[Planning Store] getProgramDetail - Error details:`, {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
          stack: err.stack,
        });
        return err;
      } finally {
        this.getProgramDetailLoading = false;
        console.log(
          `[Planning Store] getProgramDetail - Loading state set to false`
        );
      }
    },

    async updatePlanning(id, data) {
      this.updatePlanningLoading = true;
      try {
        const res = await planningAPI.updatePlanning(id, data);
        console.log(res);
        this.updatePlanningLoading = false;
        this.updatePlanningStatus.isError = false;
        this.updatePlanningStatus.message = "Plan Updated";
        this.updatePlanningStatus.code = res.data.status;
      } catch (err) {
        console.error(err);
        this.updatePlanningLoading = false;
        this.updatePlanningStatus.isError = true;
        this.updatePlanningStatus.message = err.response.data.message;
        this.updatePlanningStatus.code = err.response.data.status;
        return err;
      }
    },
    async deletePlanning(id) {
      this.deletePlanningLoading = true;
      try {
        const res = await planningAPI.deletePlanning(id);
        console.log(res);
        this.deletePlanningLoading = false;
        this.deletePlanningStatus.isError = false;
        this.deletePlanningStatus.message = "Plan Deleted";
        this.deletePlanningStatus.code = res.data.status;
      } catch (err) {
        console.error(err);
        this.deletePlanningLoading = false;
        this.deletePlanningStatus.isError = true;
        this.deletePlanningStatus.message = err.response.data.message;
        this.deletePlanningStatus.code = err.response.data.status;
        return err;
      }
    },
  },
});
